// Shared chrome for the signed-in pages (/dashboard, /admin) and small page states.

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { IconDownload, IconLogout, IconServer, IconTerminal2, IconUser } from '@tabler/icons-react';
import { AppsSheetHost, openApps } from '../ui/GetApps';
import { logout } from '../api';
import { Link, navigate, useLocation } from '../router';
import { CmxMark } from '../ui/CmxMark';
import { store, useStore } from '../store';

/** The landing's mark: the cmx glyph plus "cmx_" in Geist Pixel (the underscore blinks on hover). */
export function Brand({ to = '/' }: { to?: string }) {
  return (
    <Link to={to} className="site-brand cmx-lockup" aria-label="cmx home">
      <CmxMark />
      <span className="wm" aria-hidden="true">
        cmx<span className="wm-u">_</span>
      </span>
    </Link>
  );
}

export async function signOut() {
  await logout();
  store.set({ me: null, auth: { kind: 'out', github: false } });
  navigate('/', { replace: true });
}

/** Avatar + name in the top bar; opens a menu with My account, My cloud machine and Sign out. */
function ProfileMenu({ email, machine, admin }: { email: string; machine?: string | null; admin?: boolean }) {
  const machineHref = admin ? '/admin#machines' : '/dashboard#machine';
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const name = machine || email.split('@')[0];
  const initial = (email[0] ?? '?').toUpperCase();

  useEffect(() => {
    if (!open) return;
    const items = () => Array.from(root.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);
    items()[0]?.focus();
    const onDown = (e: MouseEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        trigger.current?.focus();
      } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const list = items();
        const i = list.indexOf(document.activeElement as HTMLElement);
        const next = e.key === 'ArrowDown' ? (i + 1) % list.length : (i - 1 + list.length) % list.length;
        list[next]?.focus();
      } else if (e.key === 'Tab') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="profile" ref={root}>
      <button
        ref={trigger}
        type="button"
        className="profile-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls="profile-menu"
        onClick={() => setOpen((o) => !o)}
        data-testid="profile-menu"
      >
        <span className="avatar" aria-hidden="true">{initial}</span>
        <span className="profile-name">{name}</span>
      </button>
      {open && (
        <div className="profile-pop" id="profile-menu" role="menu" aria-label="Account menu">
          <div className="profile-pop-head">
            <span className="avatar avatar-lg" aria-hidden="true">{initial}</span>
            <div>
              <div className="profile-pop-name">{name}</div>
              <div className="profile-pop-mail" title={email}>{email}</div>
            </div>
          </div>
          <div className="profile-pop-list">
            <a role="menuitem" className="profile-item" href="/account" onClick={(e) => { e.preventDefault(); setOpen(false); navigate('/account'); }} data-testid="menu-account">
              <IconUser size={16} stroke={1.5} aria-hidden="true" />
              My account
            </a>
            <button
              type="button"
              role="menuitem"
              className="profile-item"
              onClick={() => {
                setOpen(false);
                openApps();
              }}
              data-testid="menu-apps"
            >
              <IconDownload size={16} stroke={1.5} aria-hidden="true" />
              Get the apps
            </button>
            {machine ? (
              <a role="menuitem" className="profile-item" href={machineHref} onClick={(e) => { e.preventDefault(); setOpen(false); navigate(machineHref); }} data-testid="menu-machine">
                <IconServer size={16} stroke={1.5} aria-hidden="true" />
                My cloud machine
              </a>
            ) : null}
            <hr className="profile-sep" />
            <button
              type="button"
              role="menuitem"
              className="profile-item"
              disabled={busy}
              data-testid="sign-out"
              onClick={() => {
                setBusy(true);
                void signOut();
              }}
            >
              <IconLogout size={16} stroke={1.5} aria-hidden="true" />
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function TopBar() {
  const me = useStore((s) => s.me);
  const { path } = useLocation();
  // inMenu: also in the profile menu (My account), so a phone's bar can leave it out.
  const tabs = [{ to: '/dashboard', label: 'Overview' }, { to: '/guide', label: 'Guide' }, ...(me ? [{ to: '/account', label: 'Account', inMenu: true }] : []), ...(me?.role === 'admin' ? [{ to: '/admin', label: 'Admin' }] : [])];
  return (
    <header className="topbar">
      <div className="topbar-inner">
        <Brand to="/dashboard" />
        <nav className="topbar-tabs" aria-label="Dashboard">
          {tabs.map((t) => (
            <Link key={t.to} to={t.to} className={'inMenu' in t ? 'topbar-tab topbar-tab-in-menu' : 'topbar-tab'} aria-current={path === t.to ? 'page' : undefined} data-testid={`tab-${t.label.toLowerCase()}`}>
              {t.label}
            </Link>
          ))}
        </nav>
        <span className="spacer" />
        {me?.machine ? (
          <a className="btn btn-primary topbar-open" href="/app" data-testid="topbar-open-workspace">
            <IconTerminal2 size={16} stroke={1.5} aria-hidden="true" />
            Open workspace
          </a>
        ) : null}
        {me ? <ProfileMenu email={me.email} machine={me.machine?.id} admin={me.role === 'admin'} /> : null}
      </div>
    </header>
  );
}

export function DashLayout({ title, subtitle, children }: { title: string; subtitle?: ReactNode; children: ReactNode }) {
  return (
    <div className="dash">
      <TopBar />
      <AppsSheetHost />
      <main className="dash-main" id="main">
        <div className="dash-head">
          <h1>{title}</h1>
          {subtitle && <p className="muted">{subtitle}</p>}
        </div>
        {children}
      </main>
    </div>
  );
}

export function PageSplash({ title, detail }: { title?: string; detail?: string }) {
  return (
    <div className="splash" aria-busy={!title}>
      {title ? (
        <div>
          <div className="splash-title">{title}</div>
          {detail && <div className="muted">{detail}</div>}
        </div>
      ) : (
        'cmx'
      )}
    </div>
  );
}

export function NotFound() {
  return (
    <main className="auth-page">
      <div className="auth-card">
        <Brand />
        <h1 className="auth-title">Page not found</h1>
        <p className="muted">There’s nothing at this address.</p>
        <Link to="/" className="btn btn-block">
          Back to cmx.sh
        </Link>
      </div>
    </main>
  );
}

/** "3 minutes ago"-style relative time. */
export function ago(iso: string | null | undefined): string {
  if (!iso) return 'never';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '—';
  const s = Math.round((Date.now() - t) / 1000);
  if (s < 45) return 'just now';
  const units: [number, string][] = [
    [60, 'minute'],
    [3600, 'hour'],
    [86400, 'day'],
    [2592000, 'month'],
    [31536000, 'year'],
  ];
  let label = '';
  for (let i = units.length - 1; i >= 0; i--) {
    const [sec, name] = units[i];
    if (s >= sec) {
      const n = Math.round(s / sec);
      label = `${n} ${name}${n === 1 ? '' : 's'} ago`;
      break;
    }
  }
  return label || 'just now';
}

export function fullDate(iso: string | null | undefined) {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

/** "standard-m" → "Standard M". */
export const tierLabel = (id: string) => id.split('-').map((w, i) => (i ? w.toUpperCase() : w[0].toUpperCase() + w.slice(1))).join(' ');

export function specs(m: { vcpu?: number; memoryGB?: number; diskGB?: number; tier?: string }) {
  const parts = [];
  if (m.tier) parts.push(tierLabel(m.tier));
  if (m.vcpu) parts.push(`${m.vcpu} vCPU`);
  if (m.memoryGB) parts.push(`${m.memoryGB} GB RAM`);
  if (m.diskGB) parts.push(`${m.diskGB} GB disk`);
  return parts.join(' · ') || '—';
}
